import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import RichTextEditor from '@/components/RichTextEditor';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Newspaper, 
  Plus, 
  Edit, 
  Trash2, 
  Loader2,
  Eye,
  EyeOff,
  ArrowLeft,
  ExternalLink,
  Calendar,
  Link as LinkIcon,
  Save
} from 'lucide-react';
import { extractErrorMessage } from '@/lib/api-errors';

export default function AdminPageManager() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Dialog states
  const [pageDialogOpen, setPageDialogOpen] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  
  // Form state
  const [pageForm, setPageForm] = useState({
    slug: '',
    title: '',
    meta_description: '',
    content: '',
    is_published: false
  });

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    loadPages();
  }, [user, navigate]);

  const loadPages = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/cms/pages');
      setPages(res.data);
    } catch (error) {
      toast.error('Erro ao carregar páginas');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingPage(null);
    setPageForm({
      slug: '',
      title: '',
      meta_description: '',
      content: '',
      is_published: false
    });
    setPageDialogOpen(true);
  };

  const openEditDialog = async (page) => {
    try {
      const res = await api.get(`/admin/cms/pages/${page.id}`);
      setEditingPage(res.data);
      setPageForm({
        slug: res.data.slug,
        title: res.data.title,
        meta_description: res.data.meta_description || '',
        content: res.data.content,
        is_published: res.data.is_published
      });
      setPageDialogOpen(true);
    } catch (error) {
      toast.error('Erro ao carregar página');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!pageForm.slug || !pageForm.title || !pageForm.content) {
      toast.error('Preenche todos os campos obrigatórios');
      return;
    }

    // Validate slug format
    const slugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
    if (!slugRegex.test(pageForm.slug)) {
      toast.error('Slug inválido. Usa apenas letras minúsculas, números e hífens.');
      return;
    }

    setSaving(true);
    try {
      if (editingPage) {
        await api.put(`/admin/cms/pages/${editingPage.id}`, pageForm);
        toast.success('Página atualizada');
      } else {
        await api.post('/admin/cms/pages', pageForm);
        toast.success('Página criada');
      }
      setPageDialogOpen(false);
      loadPages();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Erro ao guardar página'));
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePublish = async (page) => {
    try {
      await api.put(`/admin/cms/pages/${page.id}`, { 
        is_published: !page.is_published 
      });
      toast.success(page.is_published ? 'Página despublicada' : 'Página publicada');
      loadPages();
    } catch (error) {
      toast.error('Erro ao atualizar página');
    }
  };

  const handleDelete = async (pageId) => {
    if (!confirm('Eliminar esta página? Esta ação não pode ser revertida.')) return;
    
    try {
      await api.delete(`/admin/cms/pages/${pageId}`);
      toast.success('Página eliminada');
      loadPages();
    } catch (error) {
      toast.error('Erro ao eliminar página');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('pt-PT', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-ruby" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-6xl mx-auto px-4 pt-24 pb-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/admin')}
              className="text-muted-foreground hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <Newspaper className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Gestor de Páginas</h1>
                <p className="text-muted-foreground">Cria e edita páginas personalizadas</p>
              </div>
            </div>
          </div>
          <Button onClick={openCreateDialog} className="bg-ruby hover:bg-ruby-light">
            <Plus className="w-4 h-4 mr-2" />
            Nova Página
          </Button>
        </div>

        {/* Pages List */}
        {pages.length === 0 ? (
          <Card className="bg-surface border-border">
            <CardContent className="py-16 text-center">
              <Newspaper className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-medium text-foreground mb-2">Nenhuma página criada</h3>
              <p className="text-muted-foreground mb-4">
                Cria páginas personalizadas para o teu site
              </p>
              <Button onClick={openCreateDialog} className="bg-ruby hover:bg-ruby-light">
                <Plus className="w-4 h-4 mr-2" />
                Criar Primeira Página
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {pages.map((page) => (
              <Card key={page.id} className="bg-surface border-border">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-foreground text-lg">{page.title}</h3>
                        <Badge className={page.is_published ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>
                          {page.is_published ? 'Publicada' : 'Rascunho'}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                        <span className="flex items-center gap-1 font-mono">
                          <LinkIcon className="w-4 h-4" />
                          /p/{page.slug}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(page.updated_at)}
                        </span>
                      </div>
                      {page.meta_description && (
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {page.meta_description}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      {page.is_published && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.open(`/p/${page.slug}`, '_blank')}
                          className="border-border"
                        >
                          <ExternalLink className="w-4 h-4 mr-1" />
                          Ver
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEditDialog(page)}
                        className="border-border"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Editar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleTogglePublish(page)}
                        className="border-border"
                      >
                        {page.is_published ? (
                          <>
                            <EyeOff className="w-4 h-4 mr-1" />
                            Despublicar
                          </>
                        ) : (
                          <>
                            <Eye className="w-4 h-4 mr-1" />
                            Publicar
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(page.id)}
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={pageDialogOpen} onOpenChange={setPageDialogOpen}>
          <DialogContent className="bg-surface border-border max-w-2xl z-50 max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-foreground">
                {editingPage ? 'Editar Página' : 'Nova Página'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Título *</Label>
                  <Input
                    value={pageForm.title}
                    onChange={(e) => setPageForm({...pageForm, title: e.target.value})}
                    placeholder="Título da página"
                    className="bg-background border-border"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Slug (URL) *</Label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 bg-white/5 border border-r-0 border-border rounded-l-md text-muted-foreground text-sm">
                      /p/
                    </span>
                    <Input
                      value={pageForm.slug}
                      onChange={(e) => setPageForm({...pageForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')})}
                      placeholder="nome-da-pagina"
                      className="bg-background border-border rounded-l-none font-mono"
                      required
                      disabled={!!editingPage}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Apenas letras minúsculas, números e hífens
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Meta Descrição (SEO)</Label>
                <Input
                  value={pageForm.meta_description}
                  onChange={(e) => setPageForm({...pageForm, meta_description: e.target.value})}
                  placeholder="Descrição para motores de busca (opcional)"
                  className="bg-background border-border"
                  maxLength={160}
                />
                <p className="text-xs text-muted-foreground">
                  {pageForm.meta_description.length}/160 caracteres
                </p>
              </div>
              <div className="space-y-2">
                <Label>Conteúdo *</Label>
                <RichTextEditor
                  content={pageForm.content}
                  onChange={(html) => setPageForm({...pageForm, content: html})}
                  placeholder="Escreve o conteúdo da página aqui..."
                  minHeight="300px"
                />
              </div>
              <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                <div>
                  <Label className="text-foreground">Publicar página</Label>
                  <p className="text-xs text-muted-foreground">
                    Páginas publicadas são visíveis para todos os visitantes
                  </p>
                </div>
                <Switch
                  checked={pageForm.is_published}
                  onCheckedChange={(checked) => setPageForm({...pageForm, is_published: checked})}
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setPageDialogOpen(false)} className="border-border">
                  Cancelar
                </Button>
                <Button type="submit" disabled={saving} className="bg-ruby hover:bg-ruby-light">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  {editingPage ? 'Guardar Alterações' : 'Criar Página'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
